import { useCallback, useEffect, useState } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import calendarIcon from "../../images/icons/calendarIcon.svg";
import { type Locale, uk, enGB, pl } from "date-fns/locale";
import { useTranslation } from "react-i18next";
import { getDatepickerDateFormat } from "../../services/format/time";
import "./CustomDatepicker.scss";
import "react-datepicker/dist/react-datepicker.css";

registerLocale("uk", uk);
registerLocale("en", enGB);
registerLocale("pl", pl);

interface Props {
    id?: string;
    nameElement?: string;
    value?: Date;
    minDate?: Date;
    maxDate?: Date;
    onChange: (date: Date | null) => void;
    onBlur?: () => void;
    isInvalid?: boolean;
    isValid?: boolean;
    customInput?: React.ReactElement;
    disabled?: boolean;
    placeholderText?: string;
}

const CustomDatepicker = ({
    id,
    nameElement,
    value,
    minDate,
    maxDate,
    onChange,
    onBlur,
    isInvalid,
    isValid,
    customInput,
    disabled,
    placeholderText,
}: Props) => {
    const idElement = id ? id : "calendar";
    const { i18n } = useTranslation();

    const localeMap: Record<string, Locale> = { UK: uk, EN: enGB, PL: pl };
    const currentLocale = localeMap[i18n.language] ?? enGB;

    const [startDate, setStartDate] = useState<Date | null>(null);
    const dateFormat = getDatepickerDateFormat();

    useEffect(() => {
        setStartDate(value ? value : null);
    }, [value]);

    const handleOnChange = useCallback(
        (date: Date | null) => {
            if (!(minDate && date === null)) {
                setStartDate(date);
            }
            onChange(date);
        },
        [minDate, onChange]
    );

    return (
        <>
            <DatePicker
                showIcon
                id={idElement}
                name={nameElement}
                selected={startDate}
                className={`form-control${isInvalid ? " is-invalid" : ""}${isValid ? " is-valid" : ""}`}
                wrapperClassName="custom-datepicker-wrapper"
                popperClassName="custom-react-datepicker-popper"
                icon={<img className={customInput ? "custom-input" : ""} src={calendarIcon} />}
                onChange={(date: Date | null) => handleOnChange(date)}
                onBlur={onBlur}
                autoComplete="off"
                minDate={minDate}
                maxDate={maxDate}
                dateFormat={dateFormat}
                placeholderText={placeholderText || dateFormat.toUpperCase()}
                locale={currentLocale}
                customInput={customInput}
                disabled={disabled}
            />
        </>
    );
};

export default CustomDatepicker;
